import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  AlertTriangle,
  Building2,
  Newspaper,
  FileText,
  NotebookPen,
  Handshake,
  Settings,
  ChevronLeft,
  ChevronRight,
  Cog,
  Bot,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { appEnv } from '../../lib/env';
import { useUser } from '../../context/UserContext';

/** Roles that see the Autopilot benchmark (spec 8.1: L2 and admin; L1 must not learn from the verdict). */
const AUTOPILOT_ROLES = ['admin', 'senior_analyst'];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/incidents', label: 'Incidents', icon: AlertTriangle },
  { path: '/tenants', label: 'Customers', icon: Building2 },
  { path: '/updates', label: 'Updates', icon: Newspaper },
  { path: '/reports', label: 'Reports', icon: FileText },
  { path: '/notebook', label: 'Notebook', icon: NotebookPen },
  { path: '/retros', label: 'Retros', icon: Handshake },
  { path: '/autopilot', label: 'Autopilot', icon: Bot, l2Only: true },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function SidebarLogo({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      {!collapsed ? (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 230 60" className="h-8 w-auto" aria-label="Alexein">
            <path d="M224.79,12.29l.06,30.1-15.69-28.34c-.6-1.09-1.75-1.76-2.99-1.76h-7.03c-1.89,0-3.42,1.53-3.42,3.42v32h5.21v-30.33h4.03l15.89,28.75c.5.91,1.46,1.47,2.5,1.47h3.81c1.57,0,2.85-1.28,2.85-2.85V12.29h-5.21Z" fill="#ffffff"/>
            <path d="M65.25,16.6v26.8c0,2.38,1.94,4.31,4.31,4.31h21.64v-5.21h-20.75v-9.9h13.83v-5.21h-13.83v-9.9h20.75v-5.21h-21.64c-2.38,0-4.31,1.94-4.31,4.31Z" fill="#ffffff"/>
            <path d="M45.3,12.41h-5.21v30.99c0,2.38,1.94,4.31,4.31,4.31h14.69v-5.21h-13.8V12.41Z" fill="#ffffff"/>
            <path d="M171.06,12.29h-21.64v5.21h20.75v9.9h-13.8v5.21h13.8v9.9h-20.75v5.21h21.64c2.38,0,4.31-1.94,4.31-4.31v-26.8c0-2.38-1.94-4.31-4.31-4.31Z" fill="#ffffff"/>
            <rect x="182.98" y="12.3" width="5.13" height="35.41" fill="#ffffff"/>
            <path d="M28.17,12.29h-5.69c-2,0-3.84,1.09-4.79,2.85L0,47.71h5.92l8.22-15.14v.04h13.13v15.1h5.21v-31.1c0-2.38-1.94-4.31-4.31-4.31ZM16.95,27.4l5.31-9.77c.04-.08.13-.13.22-.13h4.8v9.9h-10.33Z" fill="#ffffff"/>
            <path d="M97.48,47.66v-5.16h.16c2.57,0,7.35,0,17.43-10.56.42.47.86.95,1.32,1.49.75.86,1.35,1.53,1.87,2.09l.25.27c-11.49,11.86-17.59,11.87-20.87,11.87h-.16Z" fill="#b9ff4d"/>
            <path d="M143.01,47.66c-3.52,0-10.08,0-22.8-13.92-.52-.56-1.08-1.19-1.83-2.04-.48-.55-.96-1.09-1.41-1.59-11.39-12.61-16.56-12.61-19.34-12.61h-.16v-5.16h.16c3.46,0,9.91,0,22.25,13.31l.44.47.44-.47c12.34-13.3,18.78-13.31,22.24-13.31h.16v5.16h-.16c-2.72,0-7.79,0-18.87,12.1l-.37.41.37.41c11.08,12.1,16.15,12.1,18.87,12.1h.16v5.16h-.16Z" fill="#ffffff"/>
          </svg>
          <span className="text-xs text-white/50 uppercase tracking-widest font-medium pl-0.5">
            Analyst Portal
          </span>
        </>
      ) : (
        <div className="w-8 h-8 rounded-md bg-[#b9ff4d]/20 flex items-center justify-center flex-shrink-0">
          <svg
            viewBox="0 0 32 32"
            fill="none"
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 22L16 10L24 22H8Z"
              stroke="#b9ff4d"
              strokeWidth="2"
              fill="none"
            />
            <circle cx="16" cy="18" r="2" fill="#b9ff4d" />
          </svg>
        </div>
      )}
    </div>
  );
}

interface SidebarNavProps {
  collapsed: boolean;
  /** Bumps vertical padding for touch targets (mobile drawer). */
  touch?: boolean;
}

export function SidebarNav({ collapsed, touch = false }: SidebarNavProps) {
  const { currentUser } = useUser();
  const visible = navItems.filter((item) => !('l2Only' in item && item.l2Only) || (currentUser && AUTOPILOT_ROLES.includes(currentUser.role)));
  return (
    <ul className="space-y-1 px-3">
      {visible.map(({ path, label, icon: Icon }) => (
        <li key={path}>
          <NavLink
            to={path}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 rounded-xl text-sm font-medium',
                touch ? 'py-3' : 'py-2.5',
                'transition-all duration-200',
                isActive
                  ? 'bg-[#b9ff4d] text-[#09193b] shadow-lg shadow-[#b9ff4d]/20'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              )
            }
            title={collapsed ? label : undefined}
          >
            {({ isActive }) => (
              <>
                <Icon className={cn(
                  'w-5 h-5 flex-shrink-0',
                  isActive && 'text-[#09193b]'
                )} />
                {!collapsed && <span>{label}</span>}
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

export function SidebarPortalLink({ collapsed, touch = false }: SidebarNavProps) {
  return (
    <a
      href={appEnv.operationsPortalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-3 px-3 rounded-xl text-sm font-medium',
        touch ? 'py-3' : 'py-2.5',
        'text-white/70 hover:text-white hover:bg-white/10',
        'transition-all duration-200'
      )}
      title={collapsed ? 'Operations Portal' : undefined}
    >
      <Cog className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span>Operations Portal</span>}
    </a>
  );
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen',
        'bg-[#09193b] dark:bg-[#050d1d]',
        'hidden md:flex flex-col transition-all duration-300',
        collapsed ? 'w-16' : 'w-56'
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-white/10">
        <SidebarLogo collapsed={collapsed} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto">
        <SidebarNav collapsed={collapsed} />
      </nav>

      {/* External Portal Link */}
      <div className="px-3 py-2 border-t border-white/10">
        <SidebarPortalLink collapsed={collapsed} />
      </div>

      {/* Toggle button */}
      <div className="p-3 border-t border-white/10">
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl',
            'text-white/70 hover:text-white hover:bg-white/10',
            'transition-all duration-200'
          )}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
